import { useState, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, ImagePlus, Undo2, Redo2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadImage, formatApiError } from "@/lib/api";

function ToolBtn({ onClick, active = false, title, testid, children }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`grid place-items-center h-8 w-8 border transition-colors ${
        active ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/15 text-ptf-primary hover:bg-ptf-primary/5"
      }`}
    >
      {children}
    </button>
  );
}

// Rich text editor for admin story content. Emits HTML via onChange.
export default function RichTextEditor({ label = "Content", value = "", onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit, Image],
    content: value || "",
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: "ptf-prosemirror", "data-testid": "rich-editor" } },
  });

  const insertImage = async (file) => {
    if (!file || !editor) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      editor.chain().focus().setImage({ src: url }).run();
      toast.success("Image inserted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Image upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!editor) return null;

  return (
    <div>
      <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">{label}</span>
      <div className="mt-1 border border-ptf-primary/20 focus-within:border-ptf-secondary transition-colors">
        <div className="flex flex-wrap items-center gap-1 border-b border-ptf-primary/10 p-2 bg-ptf-bg/50">
          <ToolBtn testid="rte-bold" title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></ToolBtn>
          <ToolBtn testid="rte-italic" title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></ToolBtn>
          <span className="w-px h-6 bg-ptf-primary/10 mx-1" />
          <ToolBtn testid="rte-h2" title="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></ToolBtn>
          <ToolBtn testid="rte-h3" title="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></ToolBtn>
          <span className="w-px h-6 bg-ptf-primary/10 mx-1" />
          <ToolBtn testid="rte-bullet" title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></ToolBtn>
          <ToolBtn testid="rte-ordered" title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></ToolBtn>
          <ToolBtn testid="rte-quote" title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></ToolBtn>
          <span className="w-px h-6 bg-ptf-primary/10 mx-1" />
          <ToolBtn testid="rte-image" title="Insert image" onClick={() => fileRef.current?.click()}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          </ToolBtn>
          <span className="w-px h-6 bg-ptf-primary/10 mx-1" />
          <ToolBtn testid="rte-undo" title="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo2 className="h-4 w-4" /></ToolBtn>
          <ToolBtn testid="rte-redo" title="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo2 className="h-4 w-4" /></ToolBtn>
        </div>
        <EditorContent editor={editor} />
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => insertImage(e.target.files?.[0])}
      />
    </div>
  );
}
